import { eTipoPermissao } from '@/enums/ePermissoes';
import { PermissaoAcaoTelaModel } from '@/models/login/login.model';
import sessionService from '@/services/helper/session.service';
import { computed, ComputedRef } from 'vue';
import { useRoute } from 'vue-router';

/**
 * Permissão de ação de uma tela (visualizar/incluir/alterar/excluir) enriquecida com o atalho
 * `podeEditar` (incluir OU alterar) — o mesmo par de flags que o {@link BarraBotoes} usa para
 * decidir o botão Salvar.
 */
export interface PermissaoTela {
  visualizar: boolean;
  incluir: boolean;
  alterar: boolean;
  excluir: boolean;
  /** Atalho: pode gravar (incluir OU alterar). Espelha a lógica do Salvar do BarraBotoes. */
  podeEditar: boolean;
}

/**
 * Chave e default do provide/inject usados pelas telas que propagam a permissão para componentes
 * filhos (cards/abas). O default é **permissivo** (tudo liberado): componentes usados fora de uma
 * tela que provê a permissão continuam funcionando, e uma eventual falha na resolução não tranca a
 * tela — mesmo espírito do fallback permissivo do resto do módulo (ex.: modo somente-leitura do
 * professor).
 */
export const PERMISSAO_TELA_INJECT_KEY = 'permissaoTela';

export function permissaoTelaLiberada(): PermissaoTela {
  return enriquecer(new PermissaoAcaoTelaModel(true, true, true, true));
}

/**
 * Núcleo reutilizável: resolve a permissão de ação a partir da lista de ids de permissão de uma
 * rota (`meta.permissions`). Reaproveita exatamente a mesma regra do BarraBotoes
 * (`sessionService.RetornarPermissaoAcaoTela`): usuário suporte/desenvolvimento e telas sem
 * permissão configurada recebem acesso total; caso contrário, vale o grupo selecionado, liberando
 * se **qualquer** um dos ids configurados tiver o registro de permissão.
 */
export function resolverPermissaoTela(
  permissions: eTipoPermissao[] | undefined,
): PermissaoTela {
  return enriquecer(sessionService.RetornarPermissaoAcaoTela(permissions));
}

/**
 * Versão composable (Composition API / `<script setup>`): reativa à rota atual. Nos componentes
 * Options API dominantes no repo, prefira `resolverPermissaoTela(this.$route.meta?.permissions)`
 * num `computed`, e propague para os filhos via provide/inject com a chave
 * {@link PERMISSAO_TELA_INJECT_KEY}.
 */
export function usePermissaoTela(): ComputedRef<PermissaoTela> {
  const route = useRoute();
  return computed(() =>
    resolverPermissaoTela(route.meta?.permissions as eTipoPermissao[] | undefined),
  );
}

function enriquecer(p: PermissaoAcaoTelaModel): PermissaoTela {
  return {
    visualizar: p.visualizar,
    incluir: p.incluir,
    alterar: p.alterar,
    excluir: p.excluir,
    podeEditar: p.incluir || p.alterar,
  };
}
